import React, { useState } from 'react'
import { CreditApplication, CreditDocument } from '_gql'
import { css } from '@emotion/core'

import { useMakeTestId } from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'

import { useAuth } from '@sputnik/auth'
import ImageWrapper from '@sputnik/components/ImageWrapper'
import StatusButtons from '@sputnik/components/StatusButtons'
import useGoToOffers from '@sputnik/hooks/useGoToOffers'
import StatusTile from '@sputnik/pages/Dashboard/StatusTile/StatusTile'

import DocumentSelectionPopSheet from './DocumentSelectionPopSheet'

interface MoreInfoNeededStatusDocumentsTileProps {
  creditApp: CreditApplication
  refetch: () => void
}

const MoreInfoNeededStatusDocumentsTile = ({ creditApp, refetch }: MoreInfoNeededStatusDocumentsTileProps) => {
  const makeTestId = useMakeTestId('MoreInfoNeededStatusDocumentsTile')
  const { requestApplicantToken } = useAuth()
  const {
    requiredDocs: requiredDocuments,
    salesforceCreditId,
    lastUpdatedByApplicantAt,
    authorizedSigner,
  } = creditApp || {}

  const { decisions, handleGoToOffers, isLoggedInUserAuthorizedSigner } = useGoToOffers({
    salesforceCreditId,
    authSignerEmail: authorizedSigner?.email ?? '',
  })
  const hasSelectedDecision = !!decisions?.find((decision) => decision.userSelectedAt)
  const showGoToOfferButton = isLoggedInUserAuthorizedSigner && decisions?.length > 0 && !hasSelectedDecision
  const [showUploader, setShowUploader] = useState(false)

  const [uploaderReady, setUploaderReady] = useState(false)
  const [remainingDocs, setRemainingDocs] = useState<CreditDocument[]>(
    requiredDocuments?.map((document) => document?.documentType) || [],
  )

  const handleUploadDocuments = async () => {
    setShowUploader(true)
    await requestApplicantToken(salesforceCreditId, true)
    setUploaderReady(true)
  }

  return (
    <div
      css={tilesHolderCss}
      data-testid={makeTestId('')}
    >
      <div data-testid={makeTestId('remaining-docs')}>
        <StatusTile
          creditApp={creditApp}
          image={
            <ImageWrapper
              alt='smart tablet with glasses and a pen'
              imageName='dashboard-application-additional-info-needed'
            />
          }
          primaryInfo={
            <FormattedMessage
              defaultMessage='Our team needs a few more things from you. {showGoToOfferButton, select, 
                  true {In the meantime, you can review and sign your offer.}
                  other {}
                }'
              id='sputnik.MoreInfoNeededStatusDocumentsTile__3G+b4d'
              values={{ showGoToOfferButton }}
            />
          }
          secondaryInfo={
            <FormattedMessage
              defaultMessage='Last updated on {date, date, long}'
              id='sputnik.MoreInfoNeededStatusDocumentsTile__7CIzhR'
              values={{ date: new Date(lastUpdatedByApplicantAt) }}
            />
          }
          title={
            <FormattedMessage
              defaultMessage='Documents needed'
              id='sputnik.MoreInfoNeededStatusDocumentsTile__iblta3'
            />
          }
        >
          <StatusButtons
            hideSecondary={!showGoToOfferButton}
            primaryText={
              <FormattedMessage
                defaultMessage='Upload documents'
                id='sputnik.MoreInfoNeededStatusDocumentsTile__QNE46O'
              />
            }
            secondaryText={
              <FormattedMessage
                defaultMessage='Go to {offers, plural, one {offer} other {offers}}'
                id='sputnik.MoreInfoNeededStatusDocumentsTile__FBxOac'
                values={{ offers: decisions?.length }}
              />
            }
            onPrimaryClick={handleUploadDocuments}
            onSecondaryClick={handleGoToOffers}
          />
        </StatusTile>
        <DocumentSelectionPopSheet
          creditApp={creditApp}
          refetch={refetch}
          remainingDocs={remainingDocs}
          setRemainingDocs={setRemainingDocs}
          setShowUploader={setShowUploader}
          setUploaderReady={setUploaderReady}
          showUploader={showUploader}
          uploaderReady={uploaderReady}
        />
      </div>
    </div>
  )
}

const tilesHolderCss = css`
  display: flex;
  flex-direction: column;
  gap: var(--tri-space-500);

  .verify-persona-button {
    margin-top: var(--tri-space-200);
  }
`

export default MoreInfoNeededStatusDocumentsTile
